import { OptionsApi, QuestionStatus, SecurityStatus, WebinarContentSummary } from '@core/swagger-client';
import { WebinarStatus, WebinarVisibility } from '@core/types';
import {
    Attachment,
    Button,
    CalendarAlt,
    Chapters,
    ChatSettings,
    Clock,
    Confirm,
    DurationIcon,
    Email,
    EyeOpen,
    Heading,
    OnDemand,
    PlannedDurationIcon,
    Play,
    Polls,
    Presentation,
    Reference,
    Security,
    SubtitlesOff,
    SubtitlesOn,
    Survey,
    Switch,
    TestWebinarNotice,
    UserAdd,
    WarningOutlined,
    WebinarNotPublishedWarning,
    WebinarTimeslotNotAvailableWarning
} from '@core/ui';
import { useNavigate } from '@online-webinar/router';
import { Link, LinkCard } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime, Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { WebinarInfoItem } from '../../../../../public/src/components/molecules/WebinarInfoItem';
import { OnDemandStatusBadge } from '../../atoms';
import { EditBannerImage, EditProfileImage, ImagesInfo } from '../../molecules';

export const ListWebinarContent = ({ webinar, isWhitelabel }: { webinar: WebinarContentSummary; isWhitelabel?: boolean }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [noOfInvited, setNoOfInvited] = useState(0);
    const [cancelWebinarConfirmationShown, setCancelWebinarConfirmationShown] = useState(false);
    const [rescheduleConfirmationShown, setRescheduleConfirmationShown] = useState(false);

    const invites = useQuery(OptionsApi.invites.get(webinar.webinarId, { enabled: !!isWhitelabel }));
    useEffect(() => {
        if (invites?.data) {
            setNoOfInvited(invites.data.length);
        }
    }, [invites]);

    // A change to one webinar shows in the webinar lists and in its content summary. The legacy keys
    // this used to invalidate (['webinar', id], ['ended-webinars']) no longer exist.
    const refreshWebinar = () => {
        queryClient.invalidateQueries({ queryKey: ['webinars'] });
        queryClient.invalidateQueries({ queryKey: ['getContentSummary', webinar.webinarId] });
    };

    const toggleIsPublishedMutation = useMutation(OptionsApi.webinar.toggleIsPublished());
    const toggleIsPublished = () => {
        toggleIsPublishedMutation.mutate(webinar.webinarId, { onSuccess: refreshWebinar });
    };

    const setWebinarVisibilityMutation = useMutation(OptionsApi.webinar.setWebinarVisibility());
    const setWebinarVisibility = (visibility: WebinarVisibility) => {
        setWebinarVisibilityMutation.mutate({ webinarId: webinar.webinarId, visibility }, { onSuccess: refreshWebinar });
    };

    const setOnDemandEnabledMutation = useMutation(OptionsApi.webinar.setOnDemandEnabled());
    const setOnDemandEnabled = (enabled: boolean) => {
        setOnDemandEnabledMutation.mutate({ webinarId: webinar.webinarId, enabled }, { onSuccess: refreshWebinar });
    };

    const cancelWebinarMutation = useMutation(OptionsApi.webinar.cancel());
    const cancelWebinar = () => {
        cancelWebinarMutation.mutate(webinar.webinarId, { onSuccess: refreshWebinar });
    };

    const broadcastDateTime = webinar.broadcastDateTime
        ? DateTime.fromISO(webinar.broadcastDateTime).toLocal().setLocale(getI18n().language)
        : DateTime.now().setLocale(getI18n().language);
    const actualDuration = Duration.fromISOTime(webinar.actualDuration || webinar.plannedDuration);
    const plannedDuration = Duration.fromISOTime(webinar.plannedDuration);

    return (
        <div className="max-w-300 flex flex-col pt-2 lg:pt-4 2xl:pt-6">
            <Confirm
                title={t('Webinar.CancelWebinar.Title')}
                open={cancelWebinarConfirmationShown}
                onClose={() => setCancelWebinarConfirmationShown(false)}
                onConfirm={() => cancelWebinar()}
                closeText={t('Webinar.CancelWebinar.Button.Close')}
                confirmText={t('Webinar.CancelWebinar.Button.Cancel')}
            >
                {t('Webinar.CancelWebinar.Description')}
            </Confirm>

            <Confirm
                title={t('Webinar.RescheduleWebinar.Title')}
                open={rescheduleConfirmationShown}
                onClose={() => setRescheduleConfirmationShown(false)}
                onConfirm={() => navigate({ to: '/$webinarId/edit', params: { webinarId: webinar.webinarId }, state: { backTo: 'content' } })}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('Generic.Button.Continue')}
            >
                {t('Webinar.RescheduleWebinar.Description')}
            </Confirm>

            {webinar.isTest && <TestWebinarNotice broadcastDateTime={webinar.broadcastDateTime} />}
            {!webinar.isPublished && webinar.hasBooking && <WebinarNotPublishedWarning />}
            {!webinar.hasBooking && (webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started) && (
                <WebinarTimeslotNotAvailableWarning />
            )}
            {webinar.isPublished && webinar.status === WebinarStatus.Cancelled && (
                <div className="bg-white bg-side-warning bg-no-repeat rounded-lg flex flex-col items-start gap-1 pl-6 md:pl-8 pr-4 md:pr-6 pt-4 pb-6 z-10">
                    <WarningOutlined classNames="w-10 fill-warning mb-2" />
                    <Heading variant="h3">{t('Webinar.Warning.WebinarIsCancelled')}</Heading>
                    <p className="max-w-3xl mb-4">{t('Webinar.Warning.WebinarIsCancelled.Body')}</p>
                </div>
            )}
            <div className="bg-white rounded-lg p-4 md:p-8 z-1">
                <Heading variant="h1">{webinar.title}</Heading>
                {webinar.status === WebinarStatus.Ended && (
                    <div className="mb-2">
                        <OnDemandStatusBadge status={webinar.onDemandStatus} />
                    </div>
                )}
                <div className="md:text-lg font-medium mb-4 md:mb-8">
                    <span className="font-light">{t('WebinarDetails.By')}</span> {webinar.nameOfHost}
                </div>
                <div className="max-w-4xl mb-8 md:mb-12">
                    <EditProfileImage webinar={webinar} />
                    <EditBannerImage webinar={webinar} />
                    <ImagesInfo />
                </div>
                <dl className="flex flex-col md:flex-row gap-2 md:gap-6 pb-6">
                    <WebinarInfoItem icon={<CalendarAlt classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Date')}>
                        {broadcastDateTime.toLocaleString(DateTime.DATE_HUGE)}
                    </WebinarInfoItem>
                    <WebinarInfoItem icon={<Clock classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Time')}>
                        {broadcastDateTime.toLocaleString(DateTime.TIME_SIMPLE)}
                    </WebinarInfoItem>
                    {webinar.status !== WebinarStatus.Ended ? (
                        <WebinarInfoItem icon={<PlannedDurationIcon classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Duration')}>
                            {plannedDuration.toFormat('h:mm')}
                        </WebinarInfoItem>
                    ) : (
                        <WebinarInfoItem icon={<DurationIcon classNames="w-6 mr-1.5 stroke-current" />} label={t('WebinarDetails.Label.Duration')}>
                            {actualDuration.toFormat('h:mm')}
                        </WebinarInfoItem>
                    )}
                    {!!webinar.clientReference && (
                        <WebinarInfoItem icon={<Reference classNames="w-6 mr-1.5 stroke-current" />} label={t('Webinar.Schedule.Label.ClientReference')}>
                            {webinar.clientReference}
                        </WebinarInfoItem>
                    )}
                </dl>
                <fieldset className="flex flex-col pb-6">
                    <legend className="flex items-center mb-0.5">
                        <EyeOpen classNames="w-6 mr-1.5 stroke-current" />
                        {t('WebinarDetails.Legend.Visibility')}
                    </legend>
                    <div className="pl-6">
                        <input
                            type="radio"
                            name="visibility"
                            id="visibility-public"
                            disabled={setWebinarVisibilityMutation.isPending}
                            value={WebinarVisibility.Public}
                            checked={webinar.visibility === WebinarVisibility.Public}
                            onChange={(e) => setWebinarVisibility(WebinarVisibility.Public)}
                            className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-radio bg-center border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                        />
                        <label htmlFor="visibility-public" className="inline-block">
                            {t('WebinarDetails.Label.Visibility.Public')}
                        </label>
                    </div>
                    <div className="pl-6">
                        <input
                            type="radio"
                            name="visibility"
                            id="visibility-private"
                            disabled={setWebinarVisibilityMutation.isPending}
                            value={WebinarVisibility.Private}
                            checked={webinar.visibility === WebinarVisibility.Private}
                            onChange={(e) => setWebinarVisibility(WebinarVisibility.Private)}
                            className="size-1em appearance-none bg-inherit checked:bg-primary checked:bg-radio bg-center border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-6 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)]"
                        />
                        <label htmlFor="visibility-private" className="inline-block">
                            {t('WebinarDetails.Label.Visibility.Private')}
                        </label>
                    </div>
                </fieldset>
                {webinar.status === WebinarStatus.Ended && (
                    <div className="flex flex-col pb-6">
                        <div className="flex items-center mb-0.5">
                            <OnDemand classNames="w-6 mr-1.5 stroke-current" />
                            {t('WebinarDetails.Section.OnDemand')}
                        </div>
                        <Switch
                            label={t('WebinarDetails.Label.OnDemand.Enabled')}
                            disabled={setOnDemandEnabledMutation.isPending}
                            checked={webinar.onDemandEnabled}
                            onChange={(e) => setOnDemandEnabled(e.target.checked)}
                        />
                    </div>
                )}
                {webinar.description && (
                    <div className="max-w-4xl pb-8 prose">
                        <Markdown>{webinar.description}</Markdown>
                    </div>
                )}
                <div className="max-w-4xl flex gap-4 mb-6">
                    {webinar.status === WebinarStatus.Expired ? (
                        <Button variant="primary" onClick={() => setRescheduleConfirmationShown(true)}>
                            {t('Webinar.Button.Reschedule')}
                        </Button>
                    ) : (
                        <Link to="/$webinarId/edit" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'content' }} variant="primary">
                            {t(
                                webinar.hasBooking || !(webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started)
                                    ? 'Content.Overview.Button.Edit'
                                    : 'Webinar.Button.Reschedule'
                            )}
                        </Link>
                    )}
                    {webinar.isPublished && webinar.status !== WebinarStatus.Cancelled && webinar.status !== WebinarStatus.Expired && (
                        <Link to="/$webinarId/share" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'content' }} variant="primary">
                            {t('Content.Overview.Button.Share')}
                        </Link>
                    )}
                    {webinar.isPublished && webinar.hasBooking && (webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started) && (
                        <Link to="/$webinarId/broadcast" params={{ webinarId: webinar.webinarId }} variant="success" classNames="ms-auto">
                            <Play classNames="h-4 fill-current me-2" />
                            {t('Content.Overview.Button.Broadcast')}
                        </Link>
                    )}
                </div>
                <div className="max-w-4xl">
                    {!webinar.isPublished && (webinar.hasBooking || webinar.status === WebinarStatus.Ended) && (
                        <Button variant="success" onClick={toggleIsPublished} isLoading={toggleIsPublishedMutation.isPending}>
                            {t('Content.Overview.Button.Publish')}
                        </Button>
                    )}
                    {webinar.isPublished && (
                        <Button variant="warning" onClick={toggleIsPublished} isLoading={toggleIsPublishedMutation.isPending}>
                            {t('Content.Overview.Button.Unpublish')}
                        </Button>
                    )}
                </div>
            </div>
            <div className="bg-accent-lighter rounded-b-lg flex flex-col -mt-4 mb-4 md:mb-6 lg:mb-8 pt-8 px-4 md:px-8 pb-4 md:pb-6">
                <Heading variant="h2" styleAs="h3" classNames="max-w-4xl flex items-center mb-4">
                    <WarningOutlined classNames="h-8 w-auto fill-warning me-2" />
                    {t('Webinar.Warning.Dangerzone')}
                </Heading>
                <div className="max-w-4xl flex justify-end gap-4">
                    {webinar.status === WebinarStatus.Planned && (
                        <Button variant="warning-outline" onClick={() => setCancelWebinarConfirmationShown(true)}>
                            {t('Webinar.Button.Cancel')}
                        </Button>
                    )}
                    <Link to="/$webinarId/delete" params={{ webinarId: webinar.webinarId }} state={{ backTo: 'content' }} variant="warning">
                        {t('Webinar.Button.Delete')}
                    </Link>
                </div>
            </div>
            <ul className="relative basis-full grid sm:grid-cols-cards gap-4 md:gap-6">
                <LinkCard
                    icon={<Presentation classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.numberOfSlides}
                    to="/$webinarId/slide-deck"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('Content.Slides.PageHeading')}
                />
                <LinkCard
                    icon={<Chapters classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.numberOfChapters}
                    to="/$webinarId/chapters"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('Content.Chapters.PageHeading')}
                />
                <LinkCard
                    icon={<Polls classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.numberOfPolls}
                    to="/$webinarId/polls"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('Content.Poll.PageHeading')}
                />
                <LinkCard
                    icon={<Attachment classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.numberOfAttachments}
                    to="/$webinarId/attachments"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('Content.Attachments.PageHeading')}
                />
                <LinkCard
                    icon={<Email classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.numberOfFollowUps}
                    to="/$webinarId/follow-up"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('FollowUpOverview.PageHeading')}
                />
                <LinkCard
                    icon={<Survey classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.surveyStatus === QuestionStatus.Enabled ? (webinar.numberOfSurveyQuestions ?? 0) : undefined}
                    to="/$webinarId/edit-survey"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    linkText={`${t('Manage.Link.ManagePrepend')} ${t('EditSurvey.Pageheading').toLocaleLowerCase()}`}
                    title={
                        webinar.surveyStatus === QuestionStatus.Enabled
                            ? t('Content.Overview.Survey.Active')
                            : webinar.surveyStatus === QuestionStatus.NotEnabled
                              ? t('Content.Overview.Survey.Inactive')
                              : t('Content.Overview.Survey.NotValid')
                    }
                />
                <LinkCard
                    icon={<Survey classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    count={webinar.examStatus === QuestionStatus.Enabled ? (webinar.numberOfExamQuestions ?? 0) : undefined}
                    to="/$webinarId/edit-exam"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    linkText={`${t('Manage.Link.ManagePrepend')} ${t('EditExam.Pageheading').toLocaleLowerCase()}`}
                    title={
                        webinar.examStatus === QuestionStatus.Enabled
                            ? t('Content.Overview.Exam.Active')
                            : webinar.examStatus === QuestionStatus.NotEnabled
                              ? t('Content.Overview.Exam.Inactive')
                              : t('Content.Overview.Exam.NotValid')
                    }
                />
                <LinkCard
                    icon={<ChatSettings classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    to="/$webinarId/chat-settings"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    title={t('ChatSettings.PageHeading')}
                />
                <LinkCard
                    icon={<Security classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                    to="/$webinarId/security"
                    params={{ webinarId: webinar.webinarId }}
                    state={{ backTo: 'content' }}
                    linkText={`${t('Manage.Link.ManagePrepend')} ${t('Security.PageHeading').toLocaleLowerCase()}`}
                    title={webinar.securityStatus === SecurityStatus.Enabled ? t('Content.Overview.Security.Active') : t('Content.Overview.Security.Inactive')}
                />
                {webinar.status === WebinarStatus.Planned || webinar.status === WebinarStatus.Started ? (
                    <LinkCard
                        icon={<SubtitlesOff classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                        to="/$webinarId/live-transcribe"
                        params={{ webinarId: webinar.webinarId }}
                        state={{ backTo: 'content' }}
                        title={t('LiveTranscribe.PageHeading')}
                        isBeta
                    />
                ) : (
                    <LinkCard
                        icon={<SubtitlesOn classNames="h-14 w-auto mt-2 mb-3 fill-card-icon" />}
                        to="/$webinarId/edit-subtitles"
                        params={{ webinarId: webinar.webinarId }}
                        state={{ backTo: 'content' }}
                        title={t('EditSubtitles.PageHeading')}
                    />
                )}
                {!!isWhitelabel && (
                    <LinkCard
                        icon={<UserAdd classNames="h-14 w-auto mt-2 mb-3 stroke-card-icon" />}
                        count={noOfInvited}
                        to="/$webinarId/invites"
                        params={{ webinarId: webinar.webinarId }}
                        state={{ backTo: 'content' }}
                        title={t('Invites.PageHeading')}
                    />
                )}
            </ul>
        </div>
    );
};
